/**
 * GitGraph — SVG-based git branch graph showing all branches as colored lanes
 * with commit nodes, fork points, and merge connections.
 *
 * Fetches commits for every branch, merges them into a single timeline
 * sorted by created_at descending, then renders an SVG rail alongside
 * commit summary cards.
 */

import { useMemo, useState } from "react";
import { Badge, Box, Flex, ScrollArea, Text } from "@radix-ui/themes";

import { useBranches, useCommits } from "../../hooks/useVersioning";
import type { Branch, Commit } from "../../api/versioning";

// -- Constants --

const LANE_W = 28;
const NODE_R = 5;
const MERGE_R = 6;
const ROW_H = 52;
const GRAPH_PAD_L = 12;

const LANE_COLORS = [
  "var(--blue-9)",
  "var(--orange-9)",
  "var(--green-9)",
  "var(--purple-9)",
  "var(--red-9)",
  "var(--cyan-9)",
  "var(--amber-9)",
];

// -- Helpers --

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortHash(hash: string | null): string {
  return hash ? hash.slice(0, 8) : "";
}

// -- Types --

interface GraphRow {
  commit: Commit;
  lane: number;
  isMerge: boolean;
}

// -- Layout computation --

function buildGraph(
  branches: Branch[],
  commitsByBranch: Map<string, Commit[]>,
): { rows: GraphRow[]; laneCount: number; branchLanes: Map<string, number> } {
  const branchLanes = new Map<string, number>();
  branchLanes.set("main", 0);

  const sortedBranches = branches
    .filter((b) => b.name !== "main")
    .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  sortedBranches.forEach((b, i) => branchLanes.set(b.name, i + 1));

  const laneCount = branchLanes.size;

  const allCommits: Commit[] = [];
  for (const [, commits] of commitsByBranch) {
    allCommits.push(...commits);
  }

  // Deduplicate by id (merge commits may appear in multiple branch fetches)
  const seen = new Set<string>();
  const unique = allCommits.filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });

  unique.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const rows: GraphRow[] = unique.map((commit) => ({
    commit,
    lane: branchLanes.get(commit.branch) ?? 0,
    isMerge: (commit.parent_ids?.length ?? 0) > 1,
  }));

  return { rows, laneCount, branchLanes };
}

// -- Hooks to fetch all branch commits --

function useAllBranchCommits(projectId: string, branches: Branch[] | undefined) {
  const branchNames = useMemo(
    () => (branches ?? []).map((b) => b.name),
    [branches],
  );

  // Fetch commits for each branch individually via the existing hook pattern.
  // We call useCommits for up to 8 branches (depth-1 model caps at ~8).
  const b0 = useCommits(projectId, branchNames[0] ?? "main");
  const b1 = useCommits(projectId, branchNames[1] ?? "__none__");
  const b2 = useCommits(projectId, branchNames[2] ?? "__none__");
  const b3 = useCommits(projectId, branchNames[3] ?? "__none__");
  const b4 = useCommits(projectId, branchNames[4] ?? "__none__");
  const b5 = useCommits(projectId, branchNames[5] ?? "__none__");
  const b6 = useCommits(projectId, branchNames[6] ?? "__none__");
  const b7 = useCommits(projectId, branchNames[7] ?? "__none__");

  const hooks = [b0, b1, b2, b3, b4, b5, b6, b7];
  const count = branchNames.length;

  return useMemo(() => {
    const map = new Map<string, Commit[]>();
    const loading = hooks.slice(0, count).some((h) => h.isLoading);
    for (let i = 0; i < count && i < 8; i++) {
      const name = branchNames[i]!;
      if (hooks[i]!.data) map.set(name, hooks[i]!.data!);
    }
    return { commitsByBranch: map, isLoading: loading };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, ...hooks.map((h) => h.data), ...hooks.map((h) => h.isLoading)]);
}

// -- SVG rendering --

function GraphSvg({
  rows,
  laneCount,
  branchLanes,
  branches,
  expandedId,
  expandedHeights,
}: {
  rows: GraphRow[];
  laneCount: number;
  branchLanes: Map<string, number>;
  branches: Branch[];
  expandedId: string | null;
  expandedHeights: Map<string, number>;
}) {
  const svgW = GRAPH_PAD_L + laneCount * LANE_W + 8;

  // Compute y positions accounting for expanded rows
  const yPositions: number[] = [];
  let cumY = ROW_H / 2;
  for (let i = 0; i < rows.length; i++) {
    yPositions.push(cumY);
    const extra = expandedId === rows[i]!.commit.id ? (expandedHeights.get(rows[i]!.commit.id) ?? 80) : 0;
    cumY += ROW_H + extra;
  }
  const totalH = cumY + ROW_H / 2;

  const laneX = (lane: number) => GRAPH_PAD_L + lane * LANE_W + LANE_W / 2;

  // Build commit-id -> row-index map for drawing merge curves
  const commitIdx = new Map<string, number>();
  rows.forEach((r, i) => commitIdx.set(r.commit.id, i));

  return (
    <svg
      width={svgW}
      height={totalH}
      style={{ flexShrink: 0, display: "block" }}
    >
      {/* Lane lines (vertical) */}
      {Array.from(branchLanes.entries()).map(([name, lane]) => {
        const branchCommits = rows
          .map((r, i) => ({ r, i }))
          .filter(({ r }) => r.commit.branch === name);
        if (branchCommits.length === 0) return null;
        const firstY = yPositions[branchCommits[0]!.i]!;
        const lastY = yPositions[branchCommits[branchCommits.length - 1]!.i]!;
        return (
          <line
            key={`lane-${name}`}
            x1={laneX(lane)}
            y1={firstY}
            x2={laneX(lane)}
            y2={lastY}
            stroke={LANE_COLORS[lane % LANE_COLORS.length]}
            strokeWidth={2}
            strokeOpacity={0.35}
          />
        );
      })}

      {/* Merge / fork curves */}
      {rows.map((row, i) => {
        if (!row.isMerge || !row.commit.parent_ids) return null;
        const myX = laneX(row.lane);
        const myY = yPositions[i]!;
        return row.commit.parent_ids.map((parentId) => {
          const parentIdx = commitIdx.get(parentId);
          if (parentIdx === undefined) return null;
          const parent = rows[parentIdx]!;
          const pX = laneX(parent.lane);
          const pY = yPositions[parentIdx]!;
          if (pX === myX) return null; // same lane, covered by lane line
          const midY = (myY + pY) / 2;
          return (
            <path
              key={`merge-${row.commit.id}-${parentId}`}
              d={`M ${myX} ${myY} C ${myX} ${midY}, ${pX} ${midY}, ${pX} ${pY}`}
              fill="none"
              stroke={LANE_COLORS[parent.lane % LANE_COLORS.length]}
              strokeWidth={2}
              strokeOpacity={0.5}
            />
          );
        });
      })}

      {/* Fork curves: feature branch first commit connects back to main at fork point */}
      {branches
        .filter((b) => b.name !== "main" && b.fork_commit)
        .map((b) => {
          const lane = branchLanes.get(b.name);
          if (lane === undefined) return null;
          const branchRows = rows.filter((r) => r.commit.branch === b.name);
          if (branchRows.length === 0) return null;
          // Last commit in the branch (oldest) connects to the fork point on main
          const oldest = branchRows[branchRows.length - 1]!;
          const oldestIdx = rows.indexOf(oldest);
          const forkIdx = commitIdx.get(b.fork_commit!);
          if (forkIdx === undefined || oldestIdx === -1) return null;
          const bX = laneX(lane);
          const bY = yPositions[oldestIdx]!;
          const fX = laneX(0); // main lane
          const fY = yPositions[forkIdx]!;
          const midY = (bY + fY) / 2;
          return (
            <path
              key={`fork-${b.name}`}
              d={`M ${bX} ${bY} C ${bX} ${midY}, ${fX} ${midY}, ${fX} ${fY}`}
              fill="none"
              stroke={LANE_COLORS[lane % LANE_COLORS.length]}
              strokeWidth={2}
              strokeOpacity={0.4}
              strokeDasharray="4 3"
            />
          );
        })}

      {/* Commit nodes */}
      {rows.map((row, i) => {
        const cx = laneX(row.lane);
        const cy = yPositions[i]!;
        const color = LANE_COLORS[row.lane % LANE_COLORS.length]!;
        if (row.isMerge) {
          const r = MERGE_R;
          return (
            <g key={`node-${row.commit.id}`}>
              <rect
                x={cx - r}
                y={cy - r}
                width={r * 2}
                height={r * 2}
                rx={2}
                fill={color}
                transform={`rotate(45 ${cx} ${cy})`}
              />
            </g>
          );
        }
        return (
          <circle
            key={`node-${row.commit.id}`}
            cx={cx}
            cy={cy}
            r={NODE_R}
            fill={color}
            stroke="var(--color-background)"
            strokeWidth={2}
          />
        );
      })}

      {/* Branch head labels */}
      {branches.map((b) => {
        const lane = branchLanes.get(b.name);
        if (lane === undefined) return null;
        const firstRow = rows.find((r) => r.commit.branch === b.name);
        if (!firstRow) return null;
        const idx = rows.indexOf(firstRow);
        const cx = laneX(lane);
        const cy = yPositions[idx]! - 18;
        return (
          <g key={`label-${b.name}`}>
            <rect
              x={cx - 2}
              y={cy - 7}
              width={b.name.length * 6.2 + 8}
              height={14}
              rx={7}
              fill={LANE_COLORS[lane % LANE_COLORS.length]}
              fillOpacity={0.2}
              stroke={LANE_COLORS[lane % LANE_COLORS.length]}
              strokeWidth={1}
              strokeOpacity={0.5}
            />
            <text
              x={cx + 2}
              y={cy + 3.5}
              fontSize={9}
              fontFamily="var(--code-font-family, monospace)"
              fill={LANE_COLORS[lane % LANE_COLORS.length]}
              fontWeight={600}
            >
              {b.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// -- Main component --

interface Props {
  projectId: string;
}

export function GitGraph({ projectId }: Props) {
  const { data: branches } = useBranches(projectId);
  const { commitsByBranch, isLoading } = useAllBranchCommits(projectId, branches);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedHeights] = useState(() => new Map<string, number>());

  const { rows, laneCount, branchLanes } = useMemo(() => {
    if (!branches || commitsByBranch.size === 0) {
      return { rows: [] as GraphRow[], laneCount: 1, branchLanes: new Map<string, number>() };
    }
    return buildGraph(branches, commitsByBranch);
  }, [branches, commitsByBranch]);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="4">
        <Text size="2" color="gray">Loading graph...</Text>
      </Flex>
    );
  }

  if (rows.length === 0) {
    return (
      <Flex align="center" justify="center" p="4" style={{ flex: 1 }}>
        <Text size="2" color="gray">No commits yet.</Text>
      </Flex>
    );
  }

  // Compute row y positions for the card column
  const cardYs: number[] = [];
  let cum = 0;
  for (let i = 0; i < rows.length; i++) {
    cardYs.push(cum);
    const extra = expandedId === rows[i]!.commit.id ? (expandedHeights.get(rows[i]!.commit.id) ?? 80) : 0;
    cum += ROW_H + extra;
  }

  return (
    <ScrollArea style={{ flex: 1 }}>
      <div style={{ display: "flex", paddingBottom: 24, paddingTop: 8 }}>
        {/* SVG graph column */}
        <GraphSvg
          rows={rows}
          laneCount={laneCount}
          branchLanes={branchLanes}
          branches={branches ?? []}
          expandedId={expandedId}
          expandedHeights={expandedHeights}
        />

        {/* Commit cards column */}
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          {rows.map((row) => {
            const expanded = expandedId === row.commit.id;
            const isMerge = row.isMerge;
            return (
              <div
                key={row.commit.id}
                onClick={() => setExpandedId((prev) => (prev === row.commit.id ? null : row.commit.id))}
                style={{
                  height: expanded ? ROW_H + (expandedHeights.get(row.commit.id) ?? 80) : ROW_H,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  paddingRight: 12,
                  cursor: "pointer",
                  borderBottom: "1px solid var(--gray-a3)",
                  transition: "background 120ms",
                  background: expanded ? "var(--accent-a2)" : "transparent",
                  paddingLeft: 4,
                }}
              >
                <Flex align="center" gap="2">
                  <Text size="2" weight="medium" style={{ flex: 1 }} truncate>
                    {row.commit.message}
                  </Text>
                  {isMerge && <Badge size="1" color="purple">merge</Badge>}
                  <Badge
                    size="1"
                    color={row.commit.branch === "main" ? "blue" : "orange"}
                    variant="soft"
                  >
                    {row.commit.branch}
                  </Badge>
                </Flex>
                <Flex align="center" gap="3" mt="1">
                  <Text size="1" color="gray">{row.commit.author}</Text>
                  <Text size="1" color="gray" title={formatDate(row.commit.created_at)}>
                    {relativeTime(row.commit.created_at)}
                  </Text>
                  <Text
                    size="1"
                    style={{
                      fontFamily: "var(--code-font-family, monospace)",
                      color: "var(--gray-9)",
                    }}
                  >
                    {shortHash(row.commit.snapshot_hash)}
                  </Text>
                </Flex>
                {expanded && (
                  <Box mt="2" pt="2" style={{ borderTop: "1px solid var(--gray-a4)" }}>
                    <Flex direction="column" gap="1">
                      <Flex align="center" gap="2">
                        <Text size="1" color="gray">Commit:</Text>
                        <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                          {row.commit.id.slice(0, 12)}
                        </Text>
                      </Flex>
                      <Flex align="center" gap="2">
                        <Text size="1" color="gray">Hash:</Text>
                        <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)", wordBreak: "break-all" }}>
                          {row.commit.snapshot_hash || "—"}
                        </Text>
                      </Flex>
                      <Flex align="center" gap="2">
                        <Text size="1" color="gray">Date:</Text>
                        <Text size="1">{formatDate(row.commit.created_at)}</Text>
                      </Flex>
                      {isMerge && row.commit.parent_ids && (
                        <Flex align="center" gap="2">
                          <Text size="1" color="gray">Parents:</Text>
                          <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                            {row.commit.parent_ids.map((p) => p.slice(0, 8)).join(" + ")}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Box>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ScrollArea>
  );
}
